import {
  alpha,
  Box,
  IconButton,
  Link,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PublicIcon from "@mui/icons-material/Public";
import { degreesToCardinalShort } from "utils";
import dayjs from "dayjs";
import usePage from "hooks/usePage";
import MutateRunMenu from "./MutateRunMenu";
import { useRuns } from "contexts/RunsContext";

const Run = ({ run, isCurrentRun, handleRunClick }) => {
  const { isSharePage, isUrbanPage } = usePage();
  const { patchRun } = useRuns();

  const runStatusColor = {
    completed: "primary.main",
    failed: "error.main",
    cancelled: "error.main",
    running: "text.disabled",
    pending: "text.disabled",
  };

  const runLink = isSharePage
    ? `/viewer/simulation/${run.hash}`
    : `/simulation/${run.id}`;

  const runIsPending = run.status === "running" || run.status === "pending";

  const runIsFailed = run.status === "failed" || run.status === "cancelled";

  const runIsCancelled = run.status === "cancelled";

  return (
    <ListItem
      data-run-id={run.id}
      sx={(theme) => ({
        padding: "0 16px",
        backgroundColor: isCurrentRun
          ? alpha(theme.palette.primary.main, 0.15)
          : "transparent",
        display: "flex",
        alignItems: "flex-start",
      })}
    >
      <ListItemText
        primary={
          <Link
            href={runLink}
            onClick={(e) => {
              if (run.status !== "completed" && run.sim_type === "intermix") {
                e.preventDefault();
                return;
              }
              handleRunClick(e, runLink);
            }}
            sx={{
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
              color: runStatusColor[run.status] || "text.primary",
            }}
          >
            <Typography
              component="span"
              sx={{
                maxWidth: run.suitability ? "170px" : "190px",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                hyphens: "auto",
                display: "inline-block",
              }}
            >
              <strong>{run.name}</strong>
            </Typography>
          </Link>
        }
        secondary={
          <>
            <Typography component="span" variant="body2" color="textSecondary">
              {dayjs(run.created_at).format("MM-DD-YY HH:mm")}
              {" | "}
              {run.created_by}
            </Typography>
            <br />
            <Typography
              component="span"
              variant="body1"
              color="textSecondary"
              sx={{
                marginTop: "5px",
                display: "inline-block",
                lineHeight: 1.3,
              }}
            >
              {isUrbanPage
                ? `${run.duration} mins - ${degreesToCardinalShort(run.wind_direction)} - ${run.wind_speed} mph`
                : `${run.duration / 60 + "hr - "}
              ${degreesToCardinalShort(run.wind_direction) + " - "}
              ${run.wind_speed + "mph - "}
              ${run.fm_1hr + "% RH - "}
              ${run.fm_100hr + "% 100hr - "}
              ${run.landscape_version}`}
              {isUrbanPage && <br />}
              {isUrbanPage && `Iterations: ${run.iterations || 0}`}
            </Typography>
            <br />
            {run.description && (
              <Typography
                component="span"
                variant="body1"
                color="textSecondary"
              >
                {run.description}
              </Typography>
            )}
            {runIsPending && (
              <Typography
                component="span"
                variant="body1"
                color="textSecondary"
                sx={{ marginTop: "5px", fontStyle: "italic", display: "block" }}
              >
                Simulation in progress...
              </Typography>
            )}
            {runIsCancelled && !run.error_summary && !run.failed_reason && (
              <Typography
                component="span"
                variant="body1"
                color="textSecondary"
                sx={{ marginTop: "5px", fontStyle: "italic", display: "block" }}
              >
                Simulation cancelled
              </Typography>
            )}
            {(run.error_summary || run.failed_reason) && (
              <Typography
                component="span"
                variant="body1"
                color="red"
                sx={{
                  lineHeight: 1.2,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  marginTop: "2px",
                  marginBottom: "6px",
                }}
              >
                {run.error_summary || run.failed_reason}
              </Typography>
            )}
          </>
        }
      />

      {!isSharePage && (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {run.suitability && (
            <CheckCircleOutlineIcon
              sx={{
                color: "green",
                opacity: 0.6,
                marginRight: "4px",
                marginLeft: "4px",
              }}
            />
          )}
          {!runIsFailed && (
            <IconButton
              size="small"
              sx={{ color: run.shared ? "gray" : "lightgray" }}
              onClick={(e) => {
                e.stopPropagation();
                patchRun(run.id, "shared", !run.shared);
              }}
            >
              <PublicIcon />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{ color: "gray" }}
            onClick={(e) => {
              e.stopPropagation();
              patchRun(run.id, "favorite", !run.favorited);
            }}
          >
            {run.favorited ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>

          <MutateRunMenu run={run} />
        </Box>
      )}
    </ListItem>
  );
};

export default Run;
